import { CallMethodEnums } from '@/types/api';
import type {
  ActionLog, ModelLog, RequestLog, ResponseLog, SortOrder
} from '@/types/logs';

import { call } from './utils';

export type ActionLogResponse = {
  session_id: string;
  agent_actions: ActionLog[];
};

export type ModelLogResponse = {
  session_id: string;
  model_invocations: ModelLog[];
};

export type RequestLogResponse = {
  session_id: string;
  completion_requests: RequestLog[];
};

export type ResponseLogResponse = {
  completion_responses: ResponseLog[];
};

export type LogFilters = {
  agent_id?: string;
  agent_name?: string;
  constellation_name?: string;
  constellation_version?: string;
  client_id?: string;
  correlation_id?: string;
  request_id?: string;
  session_id?: string;
  from_start_time?: string;
  to_end_time?: string;
  sort_order?: SortOrder;
  sort_by?: string;
  batch_size?: number;
  offset?: number;
};

export type ModelLogFilters = LogFilters & {
  model_id?: string;
};

const DEFAULT_BATCH_SIZE = 10;

/** Build a request body, omitting empty/undefined values so the server applies defaults.
 *  `agent_id` is sent as `""` only when no agent-scoping filter is provided
 *  (agent_id, agent_name, constellation_name all empty); otherwise it's
 *  either the supplied value or omitted so the other filters narrow the scope. */
function buildLogBody( filters: ModelLogFilters, sortByDefault: string ): Record<string, unknown> {
  const hasOtherAgentScope = !!( filters.agent_name || filters.constellation_name );
  return {
    agent_id: filters.agent_id || ( hasOtherAgentScope ? undefined : '' ),
    agent_name: filters.agent_name || undefined,
    constellation_name: filters.constellation_name || undefined,
    constellation_version: filters.constellation_version || undefined,
    client_id: filters.client_id || undefined,
    model_id: filters.model_id || undefined,
    correlation_id: filters.correlation_id || undefined,
    request_id: filters.request_id || undefined,
    session_id: filters.session_id || undefined,
    from_start_time: filters.from_start_time || undefined,
    to_end_time: filters.to_end_time || undefined,
    batch_size: filters.batch_size ?? DEFAULT_BATCH_SIZE,
    offset: filters.offset ?? 0,
    sort_order: filters.sort_order ?? ( 'DESC' satisfies SortOrder ),
    sort_by: filters.sort_by || sortByDefault
  };
}

export const actionLogsGet = async( filters: LogFilters = {} ): Promise<ActionLogResponse> => {
  return call<unknown, ActionLogResponse>( {
    body: buildLogBody( filters, 'end_time' ),
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/api/logs/action_log'
  } );
};

export const requestLogsGet = async( filters: LogFilters = {} ): Promise<RequestLogResponse> => {
  return call<unknown, RequestLogResponse>( {
    body: buildLogBody( filters, 'start_time' ),
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/api/logs/completion_request_log'
  } );
};

export const responseLogsGet = async(
  call_id: string,
  max_results: number = 500
): Promise<ResponseLogResponse> => {
  return call<unknown, ResponseLogResponse>( {
    body: {
      call_id,
      max_results
    },
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/api/logs/completion_response_log'
  } );
};

export const modelLogsGet = async( filters: ModelLogFilters = {} ): Promise<ModelLogResponse> => {
  return call<unknown, ModelLogResponse>( {
    body: buildLogBody( filters, 'end_time' ),
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/api/logs/model_log_private'
  } );
};
